import { motion } from "framer-motion";
import { Cog, Layers, Workflow, Zap, ChevronRight, Network } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function MetaSolution() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="meta-solution-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#991B1B] rounded-full filter blur-[128px] animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#1E3A8A] rounded-full filter blur-[128px] animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm mb-6">
              <Cog className="w-4 h-4" />
              <span>Transformation Systems</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Meta <span className="text-[#991B1B]">Solution</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto mb-8">
              Comprehensive transformation systems, ecosystem architecture, and AI orchestration 
              for organizational modernization and operational intelligence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Architecture */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-4">Solution Architecture</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Integrated systems for workforce transformation and digital modernization
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Layers,
                title: "Ecosystem Architecture",
                description: "Multi-layered platform architecture integrating workforce intelligence across organizational boundaries.",
                features: ["Modular Design", "Scalable Infrastructure", "Cross-Platform Integration"]
              },
              {
                icon: Workflow,
                title: "AI Orchestration",
                description: "Intelligent automation and AI-powered decision support for workforce transformation.",
                features: ["Machine Learning Models", "Predictive Analytics", "Automated Workflows"]
              },
              {
                icon: Zap,
                title: "Operational Intelligence",
                description: "Real-time operational insights and performance optimization systems.",
                features: ["Live Dashboards", "Performance Metrics", "Resource Optimization"]
              },
              {
                icon: Network,
                title: "Digital Transformation",
                description: "End-to-end digital transformation frameworks for institutional modernization.",
                features: ["Process Digitization", "Legacy Integration", "Change Management"]
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#1E3A8A]/20 rounded-xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-[#1E3A8A]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-white/60 mb-4">{item.description}</p>
                <ul className="space-y-2">
                  {item.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-white/50 text-sm">
                      <div className="w-1.5 h-1.5 bg-[#991B1B] rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Transform?
          </h2>
          <p className="text-white/60 mb-8">
            Explore how Meta Solution can modernize your organization's workforce intelligence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#991B1B] hover:bg-[#7f1d1d] text-white gap-2">
                Schedule Consultation
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/programs">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 gap-2">
                View Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
